import { useTRPC } from '@/utils/trpc';
import {
  AddImmunizationForm,
  IMMUNIZATION_ROUTE_VALUE_SET_URL,
  IMMUNIZATION_SITE_VALUE_SET_URL,
  IMMUNIZATION_VACCINE_CODE_VALUE_SET_URL,
  type AddImmunizationFormValues,
} from '@liza/ui/components/records/AddImmunizationForm';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@liza/ui/components/ui/dialog';
import { useValueSetExpand } from '@liza/ui/hooks/useValueSetExpand';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

type Props = {
  medplumPatientId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export const AddImmunizationDialog = ({ medplumPatientId, isOpen, onOpenChange }: Props): React.ReactNode => {
  const { t } = useTranslation('records');
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const vaccineCode = useValueSetExpand({
    queryOptions: (filter) =>
      trpc.valueSet.expand.queryOptions({ url: IMMUNIZATION_VACCINE_CODE_VALUE_SET_URL, filter, count: 20 }),
    enabled: isOpen,
  });
  const site = useValueSetExpand({
    queryOptions: (filter) =>
      trpc.valueSet.expand.queryOptions({ url: IMMUNIZATION_SITE_VALUE_SET_URL, filter, count: 20 }),
    enabled: isOpen,
  });
  const route = useValueSetExpand({
    queryOptions: (filter) =>
      trpc.valueSet.expand.queryOptions({ url: IMMUNIZATION_ROUTE_VALUE_SET_URL, filter, count: 20 }),
    enabled: isOpen,
  });

  const { mutateAsync: createImmunization, isPending: isCreating } = useMutation(
    trpc.patient.immunization.create.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: trpc.patient.immunization.list.queryKey({ medplumPatientId }),
        });
      },
    }),
  );

  const handleCreate = useCallback(
    async (values: AddImmunizationFormValues): Promise<void> => {
      try {
        await createImmunization({
          resource: {
            resourceType: 'Immunization',
            status: values.status,
            vaccineCode: { coding: [values.vaccineCode], text: values.vaccineCode.display ?? values.vaccineCode.code },
            patient: { reference: `Patient/${medplumPatientId}` },
            occurrenceDateTime: values.occurrenceDate,
            recorded: new Date().toISOString(),
            lotNumber: values.lotNumber || undefined,
            expirationDate: values.expirationDate || undefined,
            site: values.site ? { coding: [values.site] } : undefined,
            route: values.route ? { coding: [values.route] } : undefined,
            doseQuantity: values.doseQuantity
              ? { value: Number(values.doseQuantity), unit: values.doseUnit || undefined }
              : undefined,
            note: values.notes ? [{ text: values.notes }] : undefined,
          },
        });
        toast.success(t('immunizationAdded'));
        onOpenChange(false);
      } catch (error) {
        toast.error(`${t('addImmunizationFailed')}: ${error instanceof Error ? error.message : ''}`);
        throw error;
      }
    },
    [createImmunization, medplumPatientId, onOpenChange, t],
  );

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('addImmunization')}</DialogTitle>
        </DialogHeader>
        <AddImmunizationForm
          vaccineCode={vaccineCode}
          site={site}
          route={route}
          isCreating={isCreating}
          onClose={() => onOpenChange(false)}
          onCreate={handleCreate}
        />
      </DialogContent>
    </Dialog>
  );
};
